<template>
  <div class="px-2" v-loading="loading" style="word-break: normal;">
    <p v-if="!loading && forbidden" class="text-center text-muted py-4 mb-0">Bạn không có quyền xem nội dung này.</p>
    <p v-else-if="!loading && unavailable" class="text-center text-muted py-4 mb-0">Không tìm thấy đánh giá {{ assessmentId }}.</p>
    <p v-else-if="!loading && error" class="text-center text-muted py-4 mb-0">{{ error }}</p>
    <div v-else-if="detail">
      <div class="d-flex align-items-center border rounded bg-light px-3 py-2 mb-4">
        <i class="el-icon-time mr-2"></i>
        <span>
          Giảng viên <b>{{ detail.reviewer_name || '—' }}</b> đã nhận xét lúc
          <b>{{ detail.reviewed_at || '—' }}</b>
        </span>
      </div>

      <div class="d-flex justify-content-between align-items-center">
        <p class="mb-0" style="font-weight: 700; font-size: 20px">Kết quả đánh giá chỉ định cuối khóa</p>
        <el-tag type="success" size="small">Đã lưu</el-tag>
      </div>

      <div class="border rounded-lg p-4 mt-4" :style="{ background: currentStyle.bg, borderColor: currentStyle.bd + ' !important' }">
        <p class="mb-1" style="font-weight: 700;">CHỈ ĐỊNH BẢO HÀNH KHI FAIL KHÓA</p>
        <p class="mb-1" :style="{ color: currentStyle.fg, fontWeight: 700, fontSize: '24px' }">
          {{ detail.classification_label || '—' }}
        </p>
        <p class="mb-2">{{ detail.reason || '—' }}</p>
        <span class="text-muted" style="font-style: italic;">Chỉ định được kích hoạt nếu học viên fail kiểm tra cuối khóa.</span>
      </div>

      <div class="row mt-3">
        <div class="col-md-3 col-6 mb-2" v-for="(level, index) in scaleDefs" :key="level.code">
          <div class="border rounded p-2 h-100" :style="scaleItemStyle(level.code)" :aria-current="isScaleActive(level.code) ? 'step' : null">
            <el-tag size="mini" :effect="isScaleActive(level.code) ? 'dark' : 'plain'" :type="isScaleActive(level.code) ? '' : 'info'" :color="isScaleActive(level.code) ? currentStyle.fg : ''" class="mr-1">
              {{ index + 1 }}
            </el-tag>
            <b :style="{ color: isScaleActive(level.code) ? currentStyle.fg : '' }">{{ level.label }}</b>
            <p class="mb-0 mt-1 text-muted">{{ level.rule }}</p>
          </div>
        </div>
      </div>

      <p class="mt-4 mb-2" style="font-weight: 700;">
        HẠNG MỤC CHƯA ĐẠT ({{ detail.fail_count || 0 }}/{{ totalItemsCount }}) THEO HÌNH THỨC ÔN LUYỆN
      </p>
      <div class="row">
        <div class="col-md-6 mb-2">
          <div class="border rounded p-3 h-100">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <b>Gia sư</b>
              <el-tag type="warning" size="small">{{ detail.tutoring_competency_count || 0 }} năng lực cần bổ trợ</el-tag>
            </div>
            <p v-if="!tutoringFailures.length" class="text-muted mb-0">Không có</p>
            <div v-for="item in tutoringFailures" :key="item.code" class="border-bottom py-2">
              <b class="d-block mb-1">{{ item.skill }} · {{ item.title }}</b>
              <div v-for="(code, index) in item.tutoring_competency_codes" :key="code" class="d-flex align-items-center mb-1">
                <el-tag type="warning" size="mini" class="mr-2">{{ index + 1 }}</el-tag>
                <span>{{ tutoringCompetencyLabels[code] || code }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-6 mb-2">
          <div class="border rounded p-3 h-100">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <b>Tự học</b>
              <el-tag size="small">{{ selfStudyFailures.length }} hạng mục</el-tag>
            </div>
            <p v-if="!selfStudyFailures.length" class="text-muted mb-0">Không có</p>
            <div v-for="item in selfStudyFailures" :key="item.code" class="border-bottom py-2">
              <span class="text-muted">{{ item.skill }} ·</span> {{ item.title }}
            </div>
          </div>
        </div>
      </div>

      <p class="mt-4 mb-2" style="font-weight: 700;">TỔNG QUAN THEO KỸ NĂNG</p>
      <div class="row">
        <div class="col-md-3 col-6 mb-2" v-for="(stat, skill) in detail.per_skill_totals || {}" :key="skill">
          <div class="border rounded p-2 h-100">
            <b>{{ skill }}</b>
            <p class="mb-1" :class="stat.failed > 0 ? 'text-danger' : 'text-success'">
              {{ stat.failed > 0 ? `${stat.failed}/${stat.total} chưa đạt` : `Đạt ${stat.passed}/${stat.total}` }}
            </p>
            <el-progress :show-text="false" :percentage="skillPercent(stat)" :status="stat.failed > 0 ? 'exception' : 'success'"></el-progress>
          </div>
        </div>
      </div>

      <template v-if="plainNote">
        <p class="mt-4 mb-2" style="font-weight: 700;">GHI CHÚ CỦA GIẢNG VIÊN</p>
        <div class="border rounded p-3" style="white-space: pre-line;">{{ plainNote }}</div>
      </template>
    </div>
  </div>
</template>

<script>
import { END_COURSE_ASSESSMENT_DETAIL } from "../../../../../core/services/store/customer/customer.module";

// Same palette and scale wording as the Teacher summary, so every portal reads the same.
const CLASSIFICATION_STYLES = {
  passed: { fg: "#1E6B3E", bg: "#EEF7F1", bd: "#B9DCC7" },
  self_study: { fg: "#0B3F75", bg: "#EAF2FB", bd: "#A9C7EA" },
  tutoring_support: { fg: "#5E4900", bg: "#FBF5DD", bd: "#B99710" },
  end_course_warranty: { fg: "#8A3B00", bg: "#FFF1E0", bd: "#E3A15E" },
  repeat_course: { fg: "#9E1F14", bg: "#FDECEA", bd: "#E8A39C" },
};
const SCALE_DEFS = [
  { code: "self_study", label: "Tự ôn tập", rule: "≤ 16 hạng mục chưa đạt, tất cả thuộc Tự học" },
  { code: "tutoring_support", label: "Gia sư bổ trợ", rule: "≤ 16 hạng mục chưa đạt, có ≤ 6 năng lực thuộc Gia sư" },
  { code: "end_course_warranty", label: "Chương trình Bảo hành cuối khóa", rule: "≤ 16 hạng mục chưa đạt, có > 6 năng lực thuộc Gia sư" },
  { code: "repeat_course", label: "Học lại", rule: "> 16 hạng mục chưa đạt" },
];

export default {
  name: "EndCourseAssessmentDetail",
  props: {
    assessmentId: { default: null },
  },
  data() {
    return {
      loading: false,
      detail: null,
      error: "",
      forbidden: false,
      unavailable: false,
      scaleDefs: SCALE_DEFS,
    };
  },
  computed: {
    classification() {
      return this.detail?.classification || "passed";
    },
    currentStyle() {
      return CLASSIFICATION_STYLES[this.classification] || CLASSIFICATION_STYLES.passed;
    },
    // A competency is listed once, under the first failed item that needs it.
    tutoringFailures() {
      const seen = new Set();
      return (this.detail?.failed_items_by_mode?.tutoring || []).map((item) => {
        const codes = (item.tutoring_competency_codes || []).filter((code) => !seen.has(code));
        codes.forEach((code) => seen.add(code));
        return { ...item, tutoring_competency_codes: codes };
      });
    },
    tutoringCompetencyLabels() {
      return Object.fromEntries(
        (this.detail?.distinct_tutoring_competencies || []).map((item) => [item.code, item.label])
      );
    },
    selfStudyFailures() {
      return this.detail?.failed_items_by_mode?.self_study || [];
    },
    totalItemsCount() {
      return Object.values(this.detail?.per_skill_totals || {}).reduce((sum, stat) => sum + (stat.total || 0), 0);
    },
    // The teacher form stores the note HTML-escaped; decode it for plain-text display.
    plainNote() {
      const node = document.createElement("textarea");
      node.innerHTML = this.detail?.note || "";
      return node.value;
    },
  },
  watch: {
    assessmentId: {
      immediate: true,
      handler() {
        this.load();
      },
    },
  },
  methods: {
    isScaleActive(code) {
      return this.classification === code;
    },
    scaleItemStyle(code) {
      if (!this.isScaleActive(code)) return {};
      return { border: `2px solid ${this.currentStyle.fg} !important`, background: this.currentStyle.bg };
    },
    skillPercent(stat) {
      return stat.total ? Math.round((stat.passed * 100) / stat.total) : 0;
    },
    load() {
      this.detail = null;
      this.error = "";
      this.forbidden = false;
      this.unavailable = false;
      if (!this.assessmentId) return;
      this.loading = true;
      this.$store.dispatch(END_COURSE_ASSESSMENT_DETAIL, { assessment_id: this.assessmentId })
        .then((res) => {
          this.detail = res?.data || null;
          if (!this.detail) this.unavailable = true;
        })
        .catch((err) => {
          const status = err?.status || err?.response?.status;
          if (status === 403) this.forbidden = true;
          else if (status === 404) this.unavailable = true;
          else this.error = err?.data?.message || err?.response?.data?.message || "Không tải được đánh giá.";
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
